import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../ui';
import Badge from '../ui/Badge';
import CountdownTimer from '../raffle/CountdownTimer';

/**
 * ActiveEntriesSection - Shows user's active competition entries
 *
 * Displays a list of entries in currently active competitions
 * with quick access to view each competition.
 */
export default function ActiveEntriesSection({ entries, raffles, className }) {
  // Filter to only active entries
  const activeEntries = entries.filter(e => e.raffleStatus === 'active');

  if (activeEntries.length === 0) {
    return (
      <Section title="Active Entries" className={className}>
        <EmptyState
          icon={TicketIcon}
          title="No active entries"
          description="You haven't entered any competitions yet."
        />
      </Section>
    );
  }

  return (
    <Section
      title="Active Entries"
      count={activeEntries.length}
      actionLabel="View All"
      actionHref="/my-entries"
      className={className}
    >
      <div className="space-y-3">
        {activeEntries.slice(0, 5).map((entry) => {
          const raffle = raffles[entry.raffleId];
          return (
            <EntryCard key={entry.id} entry={entry} raffle={raffle} />
          );
        })}
      </div>
      {activeEntries.length > 5 && (
        <p className="text-sm text-cream/50 text-center mt-4">
          +{activeEntries.length - 5} more entries
        </p>
      )}
    </Section>
  );
}

function EntryCard({ entry, raffle }) {
  if (!raffle) {
    return (
      <div className="bg-midnight-lighter/50 rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-midnight-lighter rounded w-3/4" />
      </div>
    );
  }

  const photo = raffle.photos?.[0];
  const odds = raffle.ticketsSold > 0
    ? Math.round((entry.ticketCount / raffle.ticketsSold) * 100)
    : 0;

  return (
    <Link
      to={`/competition/${raffle.id}`}
      className="group flex items-center gap-4 bg-midnight-lighter/50 hover:bg-midnight-lighter rounded-xl p-3 border border-transparent hover:border-primary/20 transition-all"
    >
      {/* Photo */}
      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-midnight-lighter">
        {photo && (
          <img
            src={photo}
            alt={raffle.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-cream truncate group-hover:text-primary transition-colors">
          {raffle.title}
        </h4>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-sm font-mono text-cream/60">
            {entry.ticketCount} {entry.ticketCount === 1 ? 'entry' : 'entries'}
          </span>
          <span className="text-cream/30">•</span>
          <span className="text-sm text-cream/60">
            {odds}% odds
          </span>
        </div>
      </div>

      {/* Timer */}
      <div className="shrink-0 text-right">
        <CountdownTimer
          endsAt={raffle.endsAt}
          compact
          className="text-sm"
        />
      </div>
    </Link>
  );
}

// Reusable Section wrapper
function Section({ title, count, actionLabel, actionHref, children, className }) {
  return (
    <div className={cn('bg-midnight-light/50 rounded-2xl border border-cream/5 p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="font-display font-semibold text-lg text-cream">{title}</h3>
          {count !== undefined && count > 0 && (
            <Badge variant="primary" size="sm">{count}</Badge>
          )}
        </div>
        {actionLabel && actionHref && (
          <Link
            to={actionHref}
            className="text-sm text-primary hover:text-primary-light transition-colors"
          >
            {actionLabel} →
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

// Empty state component
function EmptyState({ icon: Icon, title, description, actionLabel, actionHref }) {
  return (
    <div className="text-center py-8">
      {Icon && (
        <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-primary" />
        </div>
      )}
      <h4 className="font-semibold text-cream mb-2">{title}</h4>
      <p className="text-cream/50 text-sm mb-4 max-w-xs mx-auto">{description}</p>
      {actionLabel && actionHref && (
        <Link to={actionHref}>
          <Button size="sm">{actionLabel}</Button>
        </Link>
      )}
    </div>
  );
}

// Icon
function TicketIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  );
}

export { Section, EmptyState };
